import { Member } from '@/features/iam/members/Member'
import { type User } from '@/features/iam/users/User'
import { Model, type ModelProps } from '@/infra/model'
import { once } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import { Subject } from 'rxjs'
import { Id } from '@/infra/id'
import { unixEpoch } from '@/utils/date-util'
import { type MessageContent } from './content'
import { Conversation } from './Conversation'
import { type MessageStatus } from './message-status'
import { type MessageType } from './message-type'

export type MessageProps = ModelProps<Message>

/**
 * Message.
 */
export class Message extends Model<Message> {
  /**
   * Subject used to push status changes.
   */
  private readonly _statusChanges = new Subject<MessageStatus>()

  /**
   * Status changes as an observable stream.
   */
  readonly statusChanges$ = this._statusChanges.asObservable()

  id = Id.none

  @observable accessor recipientStatuses: MessageStatus[] = []
  @observable accessor sender = Member.none
  @observable accessor dateCreated = unixEpoch()
  @observable accessor dateModified = unixEpoch()
  @observable accessor messageType: MessageType = 'Message'

  /**
   * The message content.
   */
  @observable.shallow accessor content: MessageContent = {
    type: 'Text',
    text: '',
  }

  /**
   * Default message.
   */
  static get none() {
    return lazyNone()
  }

  constructor(readonly conversation: Conversation) {
    super()
  }

  /**
   * Returns a (Markdown) string that can be used for displaying
   * text for the message, even if it's not a text content type.
   */
  @computed
  get text() {
    const content = this.content
    switch (content.type) {
      case 'Text':
      case 'Assistant':
        return content.text
      case 'Call':
        return 'Call started'
      case 'Progression':
        return 'Job Progression'
    }
  }

  /**
   * Gets the status for a particular user.
   *
   * @param user the user ID that the status must belong to
   */
  statusFor(user: User): MessageStatus | null {
    const status = this.recipientStatuses.find((s) => s.member.user === user)
    if (status) {
      return status
    }

    return null
  }

  /**
   * Marks the message as delivered for the specified user.
   *
   * @param user
   */
  @action.bound
  markDelivered(user: User) {
    const status = this.statusFor(user)
    if (
      status &&
      status.deliveryStatus !== 'Delivered' &&
      status.deliveryStatus !== 'Read'
    ) {
      status.deliveryStatus = 'Delivered'
      this._statusChanges.next(status)
    }
  }

  /**
   * Marks the message as read for the specified user.
   *
   * @param user
   */
  @action.bound
  markRead(user: User) {
    const status = this.statusFor(user)
    if (status && status.deliveryStatus !== 'Read') {
      status.deliveryStatus = 'Read'
      this._statusChanges.next(status)
    }
  }
}

const lazyNone = once(() => new Message(Conversation.none))
